'use client';

import { authFetch, NoSessionError } from '@/lib/authFetch';
import type { ListingTemplateData, ListingTemplateRow } from '@/lib/listingTemplates';
import {
  TemplateApiError,
  isSchemaMissingError,
  logTemplateApiFailure,
  templateApiUserMessage,
} from '@/lib/listingTemplateErrors';

export { NoSessionError, TemplateApiError, templateApiUserMessage };

async function parseError(response: Response, operation: string): Promise<never> {
  let apiError: string | undefined;
  try {
    const body = (await response.json()) as { error?: string; message?: string };
    apiError = body.error || body.message;
  } catch {
    apiError = response.statusText || undefined;
  }
  logTemplateApiFailure(operation, response.status, apiError);

  if (response.status === 401) {
    throw new TemplateApiError(
      'Your session could not be verified. Please sign in again.',
      401
    );
  }

  const raw = apiError || `Request failed with status ${response.status}`;
  if (isSchemaMissingError(response.status, raw)) {
    if (process.env.NODE_ENV !== 'production') {
      console.error(
        '[listing-templates] Database configuration issue: listing_templates table may be missing.',
        { status: response.status, apiError }
      );
    }
    throw new TemplateApiError(
      'Template storage is not available right now.',
      response.status,
      'SCHEMA_MISSING'
    );
  }

  throw new TemplateApiError(raw, response.status);
}

function asTemplate(data: unknown): ListingTemplateRow | null {
  if (!data || typeof data !== 'object') return null;
  const obj = data as { template?: ListingTemplateRow } & Partial<ListingTemplateRow>;
  if (obj.template && typeof obj.template === 'object' && obj.template.id) {
    return obj.template;
  }
  if (obj.id && obj.name) {
    return obj as ListingTemplateRow;
  }
  return null;
}

function asTemplates(data: unknown): ListingTemplateRow[] {
  if (Array.isArray(data)) return data as ListingTemplateRow[];
  if (data && typeof data === 'object') {
    const obj = data as { templates?: unknown; template?: unknown };
    if (Array.isArray(obj.templates)) return obj.templates as ListingTemplateRow[];
    const single = asTemplate(data);
    if (single) return [single];
  }
  return [];
}

export async function listListingTemplates(): Promise<ListingTemplateRow[]> {
  const response = await authFetch('/api/listing-templates', { cache: 'no-store' });
  if (!response.ok) await parseError(response, 'list');
  const data = await response.json();
  return asTemplates(data);
}

export async function createListingTemplate(input: {
  name: string;
  description?: string;
  template_data: ListingTemplateData;
}): Promise<ListingTemplateRow> {
  const response = await authFetch('/api/listing-templates', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!response.ok) await parseError(response, 'create');
  const created = asTemplate(await response.json());
  if (!created) {
    throw new TemplateApiError('Template was saved but the response was incomplete.', 500);
  }
  return created;
}

export async function updateListingTemplate(
  id: string,
  body: Record<string, unknown>
): Promise<ListingTemplateRow> {
  const response = await authFetch(`/api/listing-templates/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) await parseError(response, 'update');
  const updated = asTemplate(await response.json());
  if (!updated) {
    throw new TemplateApiError('Template was updated but the response was incomplete.', 500);
  }
  return updated;
}

export async function deleteListingTemplate(id: string): Promise<void> {
  const response = await authFetch(`/api/listing-templates/${id}`, {
    method: 'DELETE',
  });
  if (!response.ok) await parseError(response, 'delete');
}

export async function duplicateListingTemplate(id: string): Promise<ListingTemplateRow> {
  const response = await authFetch(`/api/listing-templates/${id}/duplicate`, {
    method: 'POST',
  });
  if (!response.ok) await parseError(response, 'duplicate');
  const created = asTemplate(await response.json());
  if (!created) {
    throw new TemplateApiError('Template was duplicated but the response was incomplete.', 500);
  }
  return created;
}
